import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Progress } from '@/components/ui/Progress';
import { Badge } from '@/components/ui/Badge';
import {
  Cloud,
  Loader2,
  CheckCircle2,
  ArrowRight,
  ChevronDown
} from 'lucide-react';

const categories = {
  "Books": ["Book Clubs & Communities", "Interactive Storytelling", "Reading Trackers"],
  "Business": ["HR & Payroll", "Job Search Platforms", "Small Business Tools", "Task & Project Management"],
  "Developer Tools": ["API Testing & Management"],
  "Education": [
    "AI Study Tools",
    "Arts & Creativity Learning",
    "Coding Education",
    "Early Childhood Learning",
    "Exam Preparation",
    "Language Learning",
    "Professional Development",
    "Study Aids & Flashcards",
    "Virtual Classrooms"
  ],
  "Entertainment": [
    "AR/VR Experiences",
    "Audio Apps",
    "Celebrity News & Gossip",
    "Comedy Platforms",
    "Fan Communities",
    "Interactive Story Games",
    "Karaoke Apps",
    "Meme Generators",
    "Movie Recommendation Tools",
    "Streaming Platforms",
    "Trivia & Quiz Apps"
  ],
  "Finance": [
    "Banking Apps",
    "Bill Splitting Apps",
    "Budgeting & Expense Tracking",
    "Credit Score Monitoring",
    "Cryptocurrency Management",
    "Financial Literacy Tools",
    "Stock Market Analysis"
  ],
  "Food & Drink": [
    "Calorie Counters",
    "Cocktail Mixology",
    "Cooking Tutorials",
    "Food Delivery",
    "Food Journaling",
    "Meal Planners",
    "Restaurant Reservations"
  ],
  "Games": [
    "Action & Adventure",
    "Casual & Hyper-Casual Games",
    "Party Games",
    "Puzzles & Brain Teasers",
    "Role-Playing Games (RPGs)",
    "Simulation Games",
    "Sports Games"
  ],
  "Health & Fitness": [
    "Baby Sleep App",
    "Chronic Condition Management",
    "Diet & Nutrition",
    "Dream Analysis",
    "Fitness Challenges",
    "Habit Trackers",
    "Health Journals",
    "Meditation & Mindfulness",
    "Menstrual Cycle Trackers",
    "Pregnancy Trackers",
    "Sleep Analysis",
    "Step Counters",
    "Workout Trackers",
    "Yoga & Pilates"
  ],
  "Kids": [
    "Creative Tools",
    "Early Learning Apps",
    "Interactive Storybooks",
    "Parental Control Tools"
  ],
  "Lifestyle": [
    "Astrology & Horoscopes",
    "Beauty & Skincare",
    "Dating Apps",
    "Event Planning",
    "Home Decor & DIY",
    "Mindfulness & Self-Care",
    "Motivation",
    "Parenting & Family",
    "Personal Journals",
    "Pet Care",
    "Sustainable Living"
  ],
  "Medical": ["Pregnancy Trackers"],
  "Music": [
    "Audio Editing",
    "Concert Finder",
    "Karaoke & Singing",
    "Music Learning Tools",
    "Streaming Platforms"
  ],
  "Navigation": [
    "GPS & Maps",
    "Hiking & Outdoor Navigation",
    "Route Optimizers"
  ],
  "News": ["Aggregators", "Business News"],
  "Photo & Video": [
    "AR Filters",
    "Camera Tools",
    "Card Makers",
    "Collage Makers",
    "Meme Creators",
    "Photo Editing",
    "Photo Printing",
    "Photo Sharing",
    "Slideshow Creators",
    "Story Templates",
    "Video Editing",
    "Wallpaper"
  ],
  "Productivity": [
    "Alarm Clock",
    "Document Management",
    "Email Management",
    "Focus Timers",
    "Goal Trackers",
    "Habit Trackers",
    "Meeting Schedulers",
    "Mind Mapping",
    "Note-Taking",
    "Reminders",
    "Resume Builders",
    "Time Tracking",
    "To-Do Lists"
  ],
  "Reference": ["Religious Texts", "Scientific Resources"],
  "Shopping": [
    "AR Shopping",
    "Cashback Platforms",
    "E-commerce Marketplaces",
    "Flash Deals",
    "Grocery Shopping",
    "Luxury Goods",
    "Pet Shopping",
    "Price Comparators",
    "Shopping Lists"
  ],
  "Social Networking": [
    "Anonymous Platforms",
    "Dating Apps",
    "Event-Based Networks",
    "Friend Finders",
    "Interest-Based Groups",
    "Link Sharing/Bio Management",
    "Messaging Apps",
    "Photo Sharing",
    "Professional Networks",
    "Relationship Apps",
    "Video Communities"
  ],
  "Sports": ["Fan Communities", "Fantasy Leagues"],
  "Travel": [
    "Accommodation Booking",
    "Adventure Travel",
    "Flight Booking",
    "Itinerary Planning",
    "Local Guides",
    "Social Travel",
    "Travel Safety",
    "Trip Journals"
  ],
  "Utilities": [
    "Accessibility Tools",
    "Custom Widgets",
    "Network Tools",
    "QR Scanners",
    "Remote Control Apps",
    "System Cleaners"
  ],
  "Weather": [
    "Extreme Weather Alerts",
    "Sunrise/Sunset Timers"
  ]
};

export default function BackupProgressPage({ onComplete }) {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [backupProgress, setBackupProgress] = useState(0);
  const [isBackupComplete, setIsBackupComplete] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Start backup progress immediately on mount
  useEffect(() => {
    const interval = setInterval(() => {
      setBackupProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsBackupComplete(true);
          return 100;
        }
        return prev + 1;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []); // Empty dependency array means this runs once on mount

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setExpandedCategory(category);
  };

  const handleSubcategoryToggle = (subcategory) => {
    setSelectedSubcategories(prev => {
      if (prev.includes(subcategory)) {
        return prev.filter(sc => sc !== subcategory);
      }
      return [...prev, subcategory];
    });
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete({
        category: selectedCategory,
        subcategories: selectedSubcategories
      });
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden relative">
      <style>{`
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        @keyframes buttonGlow {
          0% {
            box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(139, 92, 246, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
          }
        }

        .button-ready {
          animation: buttonGlow 2s infinite;
        }

        .animated-gradient {
          background: linear-gradient(
            -45deg,
            #1a0f2e,
            #2d1b4e,
            #432a75,
            #31204f,
            #1f1235
          );
          background-size: 400% 400%;
          animation: gradient 15s ease infinite;
        }

        .glow {
          position: absolute;
          width: 40%;
          height: 40%;
          filter: blur(100px);
          border-radius: 50%;
        }

        .glow-1 {
          top: -10%;
          left: -10%;
          background: rgba(123, 31, 162, 0.5);
          animation: move1 10s infinite alternate;
        }

        .glow-2 {
          bottom: -10%;
          right: -10%;
          background: rgba(103, 58, 183, 0.5);
          animation: move2 15s infinite alternate;
        }

        @keyframes move1 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(10%, 10%); }
        }

        @keyframes move2 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(-10%, -10%); }
        }
      `}</style>

      <div className="animated-gradient absolute inset-0">
        <div className="glow glow-1" />
        <div className="glow glow-2" />
      </div>

      <div className="relative h-full w-full">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 blur-3xl opacity-20 w-96 h-96 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full" />
        <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 blur-3xl opacity-20 w-96 h-96 bg-gradient-to-tr from-pink-500 to-purple-500 rounded-full" />
        
        {/* Content */}
        <div className="relative h-full w-full overflow-y-auto">
          <div className="max-w-2xl mx-auto space-y-8 px-6 py-20">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 text-transparent bg-clip-text">
                Backing Up Your Content ✨
              </h1>
              <p className="text-gray-300 text-lg">
                While we secure your videos, help us understand your content focus
              </p>
            </div>

            {/* Backup Progress Card */}
            <Card className="p-6 bg-white/[0.03] backdrop-blur-xl border-white/[0.05] hover:border-purple-500/50 transition-all duration-300 rounded-2xl">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 flex items-center justify-center">
                      {isBackupComplete ? (
                        <CheckCircle2 className="h-6 w-6 text-purple-400" />
                      ) : (
                        <Cloud className="h-6 w-6 text-purple-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white/90">
                        {isBackupComplete ? 'Backup Complete!' : 'Backing up your videos...'}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {isBackupComplete 
                          ? 'Your videos are now securely stored'
                          : 'Encrypting and storing your content'}
                      </p>
                    </div>
                  </div>
                  <Badge 
                    className={`
                      transition-all duration-300
                      ${isBackupComplete 
                        ? 'bg-green-500/10 text-green-400 border-green-500/20' 
                        : 'bg-purple-500/10 text-purple-400 border-purple-500/20'}
                    `}
                  >
                    {isBackupComplete ? 'Complete' : 'In Progress'}
                  </Badge>
                </div>

                <div className="relative">
                  <Progress 
                    value={backupProgress} 
                    className="h-2 overflow-hidden rounded-full bg-purple-500/10"
                  />
                  <style>{`
                    @keyframes progressPulse {
                      0% { opacity: 0.5; }
                      50% { opacity: 1; }
                      100% { opacity: 0.5; }
                    }
                    .progress-animate {
                      animation: progressPulse 2s ease infinite;
                    }
                  `}</style>
                  <div 
                    className={`absolute inset-0 bg-gradient-to-r from-purple-500/20 to-transparent rounded-full progress-animate ${isBackupComplete ? 'hidden' : ''}`}
                    style={{ width: `${backupProgress}%` }}
                  />
                </div>

                <div className="flex justify-between text-sm text-gray-400">
                  <span>{backupProgress}% Complete</span>
                  <span>{isBackupComplete ? 'Finished' : 'In Progress...'}</span>
                </div>
              </div>
            </Card>

            {/* Category Selection Card */}
            <Card className="p-6 bg-white/[0.03] backdrop-blur-xl border-white/[0.05] hover:border-purple-500/50 transition-all duration-300 rounded-2xl">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white/90">
                      What's your content focus?
                    </h3>
                    <p className="text-sm text-gray-400">
                      Select the category that best describes your content
                    </p>
                  </div>
                  {selectedSubcategories.length > 0 && (
                    <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                      {selectedSubcategories.length} Selected
                    </Badge>
                  )}
                </div>

                <div 
                  className="space-y-2 max-h-[300px] overflow-y-auto pr-2"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(139, 92, 246, 0.3) transparent'
                  }}
                >
                  {Object.entries(categories).map(([category, subcategories]) => (
                    <div key={category} className="space-y-2">
                      <button
                        onClick={() => handleCategorySelect(category)}
                        className={`
                          w-full px-4 py-3 rounded-xl text-left
                          flex items-center justify-between
                          transition-all duration-200
                          ${selectedCategory === category 
                            ? 'bg-purple-500/20 text-white' 
                            : 'hover:bg-white/5 text-gray-300'}
                        `}
                      >
                        <span className="font-medium">{category}</span>
                        <ChevronDown 
                          className={`
                            h-5 w-5 text-purple-400
                            transition-transform duration-200
                            ${expandedCategory === category ? 'transform rotate-180' : ''}
                          `}
                        />
                      </button>

                      {expandedCategory === category && (
                        <div className="ml-4 space-y-2">
                          {subcategories.map(subcategory => (
                            <button
                              key={subcategory}
                              onClick={() => handleSubcategoryToggle(subcategory)}
                              className={`
                                w-full px-4 py-2 rounded-xl text-left text-sm
                                flex items-center justify-between
                                transition-all duration-200
                                ${selectedSubcategories.includes(subcategory)
                                  ? 'bg-purple-500/10 text-white'
                                  : 'hover:bg-white/5 text-gray-400'}
                              `}
                            >
                              {subcategory}
                              {selectedSubcategories.includes(subcategory) && (
                                <CheckCircle2 className="h-4 w-4 text-purple-400" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Continue Button with glow animation when enabled */}
            <div className="flex justify-center pt-4">
              <Button
                onClick={handleComplete}
                disabled={!selectedCategory || selectedSubcategories.length === 0 || !isBackupComplete}
                className={`
                  px-8 py-3 text-lg font-medium
                  bg-gradient-to-r from-purple-600 to-indigo-600
                  hover:from-purple-700 hover:to-indigo-700
                  text-white rounded-xl
                  shadow-lg shadow-purple-500/20
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all duration-200
                  w-full md:w-auto
                  ${(selectedCategory && selectedSubcategories.length > 0 && isBackupComplete) ? 'button-ready' : ''}
                `}
              >
                <span className="flex items-center justify-center gap-2">
                  Continue
                  <ArrowRight className="h-5 w-5" />
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 